import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { redirectIfAuthenticatedClient } from "@/lib/services/auth-guard";

export const Route = createFileRoute("/reset-password")({
  validateSearch: (s: Record<string, unknown>) => ({
    token: typeof s.token === "string" ? s.token : "",
  }),
  beforeLoad: async () => {
    await redirectIfAuthenticatedClient();
  },
  component: ResetPasswordPage,
  head: () => ({
    meta: [
      { title: "Nueva contraseña — Structura Market" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const [password, setPassword]   = useState("");
  const [password2, setPassword2] = useState("");
  const [error, setError]         = useState<string | null>(null);
  const [busy, setBusy]           = useState(false);
  const [done, setDone]           = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== password2) {
      setError("Las contraseñas no coinciden");
      return;
    }
    setBusy(true);
    try {
      if (!token) { setError("Falta el token en el enlace."); return; }
      const res = await fetch("/api/auth/reset-password", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ token, password }),
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) {
        setError(data.error ?? "No se pudo actualizar la contraseña.");
        return;
      }
      setDone(true);
      setTimeout(
        () => void navigate({ to: "/acceso/comercio", search: { returnTo: undefined, error: undefined } }),
        1500,
      );
    } catch {
      setError("Error de red. Verificá tu conexión e intentá de nuevo.");
    } finally {
      setBusy(false);
    }
  };

  if (!token && !done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-md rounded-2xl border border-border/50 bg-card p-8 text-center shadow-sm">
          <p className="text-sm text-destructive">Enlace inválido (falta token).</p>
          <Button asChild className="mt-6" variant="outline">
            <Link to="/forgot-password">Solicitar nuevo enlace</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border/50 bg-card p-8 shadow-sm">
        <h1 className="text-xl font-bold text-foreground">Nueva contraseña</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Elegí una contraseña nueva (mín. 6 caracteres).
        </p>

        {done ? (
          <p className="mt-6 rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-700 dark:bg-green-950/20 dark:border-green-900/40 dark:text-green-400">
            Contraseña actualizada. Redirigiendo al login…
          </p>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={(e) => void onSubmit(e)}>
            <div>
              <Label className="text-xs">Nueva contraseña</Label>
              <Input
                type="password"
                className="mt-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={6}
                required
              />
            </div>
            <div>
              <Label className="text-xs">Repetir contraseña</Label>
              <Input
                type="password"
                className="mt-1"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                autoComplete="new-password"
                minLength={6}
                required
              />
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? "Guardando…" : "Guardar contraseña"}
            </Button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">
          <Link
            to="/acceso/comercio"
            search={{ returnTo: undefined, error: undefined }}
            className="text-primary underline-offset-4 hover:underline"
          >
            Ir al login
          </Link>
        </p>
      </div>
    </div>
  );
}
